import React, { useState, useEffect } from 'react';
import { HashRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import './App.css';

// Import Components
import Home from './components/Home';
import Login from './components/Login';
import Register from './components/Register';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import Profile from './components/Profile';
import Matches from './components/Matches';
import Meetings from './components/Meetings';
import Navbar from './components/Navbar';
import Live from './components/Live';
import News from './components/News';
import Opportunities from './components/Opportunities';
import Events from './components/Events';

// Determine API URL based on environment
const getApiUrl = () => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }

  if (process.env.NODE_ENV === 'production') {
    return 'https://lunchup.onrender.com';
  }

  return 'http://localhost:3001';
};

const API_URL = getApiUrl();
axios.defaults.baseURL = `${API_URL}/api`;
axios.defaults.timeout = 15000; // 15 second timeout

console.log('🌐 API Configuration:');
console.log('  API_URL:', API_URL);
console.log('  NODE_ENV:', process.env.NODE_ENV);
console.log('  REACT_APP_API_URL:', process.env.REACT_APP_API_URL);

// Helper to get user from localStorage
const getUserFromStorage = () => {
  const token = localStorage.getItem('token');
  if (!token) return null;
  
  try {
    const tokenPayload = JSON.parse(atob(token.split('.')[1]));
    return {
      id: tokenPayload.userId,
      name: localStorage.getItem('userName') || 'User',
      email: localStorage.getItem('userEmail') || ''
    };
  } catch (e) {
    return null;
  }
};

function AppContent() {
  const [user, setUser] = useState(getUserFromStorage());
  const location = useLocation();

  // Check auth state on route change
  useEffect(() => {
    const currentUser = getUserFromStorage();
    setUser(currentUser);
    
    // Set axios header if token exists
    if (currentUser) {
      const token = localStorage.getItem('token');
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
  }, [location.pathname]);

  const login = (userData, token) => {
    localStorage.setItem('token', token);
    localStorage.setItem('userName', userData.name);
    localStorage.setItem('userEmail', userData.email);
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('userName');
    localStorage.removeItem('userEmail');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
  };

  return (
    <div className="App">
      <Navbar user={user} logout={logout} />
      <div className="main-content">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route
            path="/login"
            element={!user ? <Login onAuth={login} /> : <Navigate to="/matches" />}
          />
          <Route
            path="/register"
            element={!user ? <Register onAuth={login} /> : <Navigate to="/matches" />}
          />
          <Route
            path="/forgot-password"
            element={!user ? <ForgotPassword /> : <Navigate to="/matches" />}
          />
          <Route
            path="/reset-password/:token"
            element={!user ? <ResetPassword /> : <Navigate to="/matches" />}
          />
          <Route
            path="/profile"
            element={user ? <Profile user={user} /> : <Navigate to="/login" />}
          />
          <Route
            path="/matches"
            element={<Matches user={user} />}
          />
          <Route
            path="/live"
            element={<Live user={user} />}
          />
          <Route
            path="/news"
            element={<News />}
          />
          <Route
            path="/opportunities"
            element={<Opportunities />}
          />
          <Route
            path="/events"
            element={<Events user={user} />}
          />
          <Route
            path="/meetings"
            element={user ? <Meetings user={user} /> : <Navigate to="/login" />}
          />
        </Routes>
      </div>
    </div>
  );
}

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;
